import React, { useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  Backdrop,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  IconButton,
  Modal,
  Tooltip,
  Typography,
  Divider,
  Card,
  Box,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'

import DeleteIcon from '@material-ui/icons/Delete'
import ThumbDownAltIcon from '@material-ui/icons/ThumbDownAlt'
import AddIcon from '@material-ui/icons/Add'
import FileCopy from '@material-ui/icons/FileCopy'
import Edit from '@material-ui/icons/Edit'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import HistoryIcon from '@material-ui/icons/History'

import moment from 'moment'
import localization from 'moment/locale/pt-br'

import { getLabelTipoExameRealizado, getLabelTipoVestigioEspecifico, getTipoVestigioLabel } from 'helpers/labels'
import { CustomTable, ModalConfirmacao, CustomButton } from 'components'
import { FormEditarDescricaoTecnicaModal } from './FormEditarDescricaoTecnica'
import { DetailPanel } from './DetailPanel'
import { useDescricoesTecnicasVestigio } from './useDescricoesTecnicasVestigio'
import useStyles from './DescricoesTecnicasVestigio.styles'

export const DescricoesTecnicasVestigio = (props) => {
  moment.locale('pt-br', localization)
  const { idVestigio, setAtualizado, idExameRealizado } = props
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))


  const {
    descricoesTecnicas,
    processandoDescricoesTecnicas,
    openHistorico,
    setOpenHistorico,
    historico,
    processandoHistorico,
    openDeletarDescricao,
    setOpenDeletarDescricao,
    openInativarDescricao,
    setOpenInativarDescricao,
    processandoOperacao,
    handleClickDesconsiderarDescricao,
    handleClickDeletarDescricao,
    handleClickConfirmarDeletarDescricao,
    handleClickConfirmarInativarDescricao,
    handleClickHistorico,
    fetchDescricoesTecnicasDeVestigio,
  } = useDescricoesTecnicasVestigio({ idVestigio, idExameRealizado, setAtualizado })

  // necessário para que o componente filho possa chamar funções do componente pai sem causar re-render desnecessário
  const cadastroModalRef = useRef(null)

  const handleToggleExpandCard = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id)
  }

  // Definição das colunas da tabela Desktop
  const columns = [
    {
      title: 'ID',
      field: 'id',
      render: (rowData) => `#${rowData.id}`,
      cellStyle: { width: '5%', fontSize: '13px' },
      headerStyle: { width: '5%', fontWeight: 'bold' }
    },
    {
      title: 'NATUREZA (GENÉRICA)',
      field: 'tipoVestigioGenerico',
      render: (rowData) => getTipoVestigioLabel(rowData.tipoVestigioGenerico)?.toUpperCase(),
      cellStyle: { width: '20%', fontSize: '13px' },
      headerStyle: { width: '20%', fontWeight: 'bold' }
    },
    {
      title: 'NATUREZA (ESPECÍFICA)',
      field: 'tipoVestigioEspecifico',
      render: (rowData) => getLabelTipoVestigioEspecifico(rowData.tipoVestigioEspecifico)?.toUpperCase(),
      cellStyle: { width: '25%', fontSize: '13px' },
      headerStyle: { width: '25%', fontWeight: 'bold' }
    },
    {
      title: 'UNIDADES',
      field: 'unidades',
      cellStyle: { width: '10%', fontSize: '13px' },
      headerStyle: { width: '10%', fontWeight: 'bold' }
    },
    {
      title: 'DATA DA DESCRIÇÃO',
      field: 'createdAt',
      render: (rowData) => moment(rowData.createdAt).format('DD/MM/YYYY'),
      cellStyle: { width: '15%', fontSize: '13px' },
      headerStyle: { width: '15%', fontWeight: 'bold' }
    },
    {
      title: 'AÇÕES',
      field: 'acoes',
      sorting: false,
      cellStyle: { width: '25%', textAlign: 'center' },
      headerStyle: { width: '25%', fontWeight: 'bold', textAlign: 'center' },
      render: (rowData) => {
        const isDisabledEdit = rowData.id_exame_realizado !== Number(idExameRealizado)
        const isDisabledDuplicate = rowData.id_exame_realizado !== Number(idExameRealizado)
        const isDisabledDesconsiderar = rowData.id_exame_realizado === Number(idExameRealizado)
        const isDisabledRemove = rowData.id_exame_realizado !== Number(idExameRealizado)

        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Editar descrição">
              <span>
                <IconButton
                  size="small"
                  onClick={() => cadastroModalRef.current?.abrirEdicao(rowData)}
                  disabled={isDisabledEdit}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Duplicar descrição">
              <span>
                <IconButton
                  size="small"
                  onClick={() => cadastroModalRef.current?.abrirDuplicacao(rowData)}
                  disabled={isDisabledDuplicate}
                >
                  <FileCopy fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Desconsiderar descrição">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleClickDesconsiderarDescricao(rowData.id)}
                  disabled={isDisabledDesconsiderar}
                >
                  <ThumbDownAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remover descrição">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleClickDeletarDescricao(rowData.id)}
                  disabled={isDisabledRemove}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  return (
    <div className={classes.root}>
      {processandoDescricoesTecnicas ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <Grid container spacing={2}>
          {/* Ações e Botões do Topo */}
          <Grid item xs={12} className={classes.buttonContainer}>
            <CustomButton
              variant="outlined"
              colorType="primary"
              onClick={handleClickHistorico}
              startIcon={<HistoryIcon />}
            >
              Histórico
            </CustomButton>
            <CustomButton
              variant="contained"
              colorType="warning"
              onClick={() => cadastroModalRef.current?.abrirCadastro()}
              startIcon={<AddIcon />}
            >
              Adicionar descrição técnica
            </CustomButton>
          </Grid>

          {/* Formulário migrado para o ModalConfirmacao debaixo */}

          {/* Listagem de Descrições Técnicas */}
          <Grid item xs={12}>
            {isMobile ? (
              // Versão Mobile (Cards)
              descricoesTecnicas.length > 0 ? (
                descricoesTecnicas.map((descricaoTecnica) => (
                  <Card key={descricaoTecnica.id} className={classes.cardMobile}>
                    <div
                      className={classes.cardMobileHeader}
                      onClick={() => handleToggleExpandCard(descricaoTecnica.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography className={classes.cardMobileTitle}>
                        #{descricaoTecnica.id}
                      </Typography>
                      <IconButton size="small">
                        {expandedCardId === descricaoTecnica.id ? (
                          <KeyboardArrowDownIcon />
                        ) : (
                          <KeyboardArrowRightIcon />
                        )}
                      </IconButton>
                    </div>
                    <div className={classes.cardMobileContent}>
                      <div
                        className={classes.cardMobileGrid}
                        onClick={() => handleToggleExpandCard(descricaoTecnica.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={classes.cardMobileItem}>
                          <span className={classes.cardMobileLabel}>Natureza (Genérica)</span>
                          <span className={classes.cardMobileValue}>
                            {getTipoVestigioLabel(descricaoTecnica.tipoVestigioGenerico)?.toUpperCase()}
                          </span>
                        </div>
                        <div className={classes.cardMobileItem}>
                          <span className={classes.cardMobileLabel}>Natureza (Específica)</span>
                          <span className={classes.cardMobileValue}>
                            {getLabelTipoVestigioEspecifico(descricaoTecnica.tipoVestigioEspecifico)?.toUpperCase()}
                          </span>
                        </div>
                        <div className={classes.cardMobileItem}>
                          <span className={classes.cardMobileLabel}>Unidades</span>
                          <span className={classes.cardMobileValue}>
                            {descricaoTecnica.unidades}
                          </span>
                        </div>
                        <div className={classes.cardMobileItem}>
                          <span className={classes.cardMobileLabel}>Data da Descrição</span>
                          <span className={classes.cardMobileValue}>
                            {moment(descricaoTecnica.createdAt).format('DD/MM/YYYY')}
                          </span>
                        </div>
                      </div>

                      {/* Ações Mobile */}
                      <div className={classes.cardMobileActions}>
                        <Tooltip title="Editar descrição">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => cadastroModalRef.current?.abrirEdicao(descricaoTecnica)}
                              disabled={descricaoTecnica.id_exame_realizado !== Number(idExameRealizado)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Duplicar descrição">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => cadastroModalRef.current?.abrirDuplicacao(descricaoTecnica)}
                              disabled={descricaoTecnica.id_exame_realizado !== Number(idExameRealizado)}
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Desconsiderar descrição">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleClickDesconsiderarDescricao(descricaoTecnica.id)}
                              disabled={descricaoTecnica.id_exame_realizado === Number(idExameRealizado)}
                            >
                              <ThumbDownAltIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remover descrição">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleClickDeletarDescricao(descricaoTecnica.id)}
                              disabled={descricaoTecnica.id_exame_realizado !== Number(idExameRealizado)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>

                      {/* Detail Panel embutido no card Mobile se expandido */}
                      <Collapse in={expandedCardId === descricaoTecnica.id} timeout="auto" unmountOnExit>
                        <Box mt={2} pt={2} style={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                          <DetailPanel
                            idDescricaoTecnica={descricaoTecnica.id}
                            tipoVestigio={descricaoTecnica.tipoVestigioGenerico}
                            tipoExame={descricaoTecnica.exReal.tipo}
                          />
                        </Box>
                      </Collapse>
                    </div>
                  </Card>
                ))
              ) : (
                <Typography align="center" style={{ padding: '20px' }}>
                  Nenhuma descrição técnica associada a este vestígio
                </Typography>
              )
            ) : (
              // Versão Desktop (CustomTable)
              <CustomTable
                columns={columns}
                data={descricoesTecnicas}
                options={{
                  search: false,
                  paging: false,
                  toolbar: false,
                  showTitle: false,
                }}
                emptyDataSourceMessage="Nenhuma descrição técnica associada a este vestígio"
                detailPanel={(rowData) => (
                  <Box p={2} style={{ backgroundColor: theme.palette.background.default }}>
                    <DetailPanel
                      idDescricaoTecnica={rowData.id}
                      tipoVestigio={rowData.tipoVestigioGenerico}
                      tipoExame={rowData.exReal.tipo}
                    />
                  </Box>
                )}
              />
            )}
          </Grid>

          {/* Modal de Histórico de Descrições */}
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className={classes.modal}
            open={openHistorico}
            onClose={() => setOpenHistorico(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={openHistorico}>
              <div className={classes.paper}>
                <div>
                  <Typography className={classes.tituloEspelho}>
                    HISTÓRICO DE DESCRIÇÕES
                  </Typography>
                </div>
                <div className={classes.divider}>
                  <Divider />
                </div>
                <div style={{ width: '100%' }}>
                  {processandoHistorico ? (
                    <div className={classes.carregando}>
                      <CircularProgress disableShrink />
                    </div>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 'bold' }}>Natureza (Genérica)</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Natureza (Específica)</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Exame</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Descrito por</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {historico.length > 0 ? (
                            historico.map((descricao) => (
                              <TableRow key={descricao.id}>
                                <TableCell>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {getTipoVestigioLabel(descricao.tipoVestigioGenerico)?.toUpperCase()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {getLabelTipoVestigioEspecifico(descricao.tipoVestigioEspecifico)?.toUpperCase()}
                                  </Typography>
                                </TableCell>
                                <TableCell style={{ width: '20%' }}>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {descricao.exReal?.numeroExame} - {descricao.exReal ? getLabelTipoExameRealizado(descricao.exReal?.tipo).toUpperCase() : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell style={{ width: '25%' }}>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {descricao.exReal?.usuExams?.map((examinador) => examinador.nome?.toUpperCase()).join(', ')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {moment(descricao.createdAt).format('DD/MM/YYYY')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography style={{ fontSize: '12px' }}>
                                    {descricao.ativa
                                      ? 'ATIVA'
                                      : `INATIVADA EM ${moment(descricao.dataDesativacao).format('DD/MM/YYYY')} POR ${descricao.usuDesativ?.nome?.toUpperCase()}`
                                    }
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                                <Typography>Nenhum registro encontrado</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </div>
              </div>
            </Fade>
          </Modal>

          {/* Modais de Confirmação de Operações */}
          <FormEditarDescricaoTecnicaModal
            ref={cadastroModalRef}
            idVestigio={idVestigio}
            idExameRealizado={idExameRealizado}
            fetchDescricoesTecnicasDeVestigio={fetchDescricoesTecnicasDeVestigio}
            setAtualizado={setAtualizado || null}
          />

          <ModalConfirmacao
            aberto={openDeletarDescricao}
            onClose={() => setOpenDeletarDescricao(false)}
            onConfirm={handleClickConfirmarDeletarDescricao}
            processando={processandoOperacao}
            titulo="Confirmação de operação"
            confirmarColor="error"
          >
            <Typography>Tem certeza que deseja deletar essa descrição?</Typography>
          </ModalConfirmacao>

          <ModalConfirmacao
            aberto={openInativarDescricao}
            onClose={() => setOpenInativarDescricao(false)}
            onConfirm={handleClickConfirmarInativarDescricao}
            processando={processandoOperacao}
            titulo="Confirmação de operação"
            confirmarColor="warning"
          >
            <Typography>
              Tem certeza que deseja desconsiderar essa descrição? Ao confirmar, essa descrição não será mais considerada nos exames e será mostrada no histórico de descrições como inativada.
            </Typography>
          </ModalConfirmacao>
        </Grid>
      )}
    </div>
  )
}

DescricoesTecnicasVestigio.propTypes = {
  idVestigio: PropTypes.number,
  setAtualizado: PropTypes.func,
  idExameRealizado: PropTypes.number,
}
